/*
  Shop the Look — split-card behaviour (docs/design-stl README).
  Per-card image browser (arrows wrap, row click jumps, mobile swipe),
  live selection totals (never-zero rule), coverage-gated savings.
  Adding reuses the cart drawer's global [data-bundle-add] handler — this
  file only keeps that attribute's id list in sync with the ticks.
  Currency-aware via window.MijeCurrency (data-base on the money nodes).
*/

(function () {
  'use strict';

  function fmt(cents) {
    if (window.MijeCurrency) return window.MijeCurrency.format(cents);
    return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(cents / 100);
  }

  document.querySelectorAll('[data-stl-card]').forEach(function (card) {
    var rows = [...card.querySelectorAll('[data-stl-row]')];
    if (!rows.length) return;
    var discount = Number(card.dataset.discount || 0);
    var img = card.querySelector('[data-stl-img]');
    var counter = card.querySelector('[data-stl-counter]');
    var capName = card.querySelector('[data-stl-cap-name]');
    var capMeta = card.querySelector('[data-stl-cap-meta]');
    var totalEl = card.querySelector('[data-stl-total]');
    var wasEl = card.querySelector('[data-stl-was]');
    var saveWrap = card.querySelector('[data-stl-save]');
    var saveAmt = card.querySelector('[data-stl-save-amt]');
    var countEl = card.querySelector('[data-stl-count]');
    var hintEl = card.querySelector('[data-stl-hint]');
    var cta = card.querySelector('[data-bundle-add]');
    var pane = card.querySelector('[data-stl-pane]');
    var index = 0;

    function labelFor(row) {
      return row.dataset.label || 'One size';
    }

    /* the row's own meta line; the selector already names the variant, so a
       multi-option row shows the price alone */
    function renderMeta(row) {
      var el = row.querySelector('[data-stl-meta]');
      if (!el) return;
      if (row.hasAttribute('data-soldout')) {
        el.textContent = labelFor(row) + ' · Sold out';
        return;
      }
      var price = fmt(Number(row.dataset.price));
      el.textContent = row.hasAttribute('data-multi') ? price : labelFor(row) + ' · ' + price;
    }

    var pad = function (n) {
      return (n < 10 ? '0' : '') + n;
    };

    function show(i) {
      index = (i + rows.length) % rows.length;
      var row = rows[index];
      img.src = row.dataset.image;
      img.alt = row.dataset.name;
      counter.textContent = pad(index + 1) + ' / ' + pad(rows.length);
      capName.textContent = row.dataset.name;
      capMeta.textContent = row.hasAttribute('data-soldout')
        ? labelFor(row) + ' · Sold out'
        : labelFor(row) + ' · ' + fmt(Number(row.dataset.price));
      rows.forEach(function (r, j) {
        r.classList.toggle('is-showing', j === index);
      });
    }

    function selectedRows() {
      return rows.filter(function (r) {
        return r.querySelector('[data-stl-tick]').getAttribute('aria-pressed') === 'true';
      });
    }

    function recalc() {
      var sel = selectedRows();
      var sum = sel.reduce(function (s, r) {
        return s + Number(r.dataset.price);
      }, 0);

      /*
        TRUTH RULE: the discount function requires EVERY product of the bundle
        to be present in the cart (strict AND over distinct product ids), so a
        partial selection earns nothing at checkout. Showing a saving here for
        a subset was a ghost discount — the card promised money the checkout
        would never take off. Savings therefore apply only on a complete
        selection. A piece being sold out means the bundle can never be
        completed, so it can never show a saving either — which falls out of
        this same test, since a sold-out row can't be ticked.

        Rounding is PER LINE because the function discounts each cart line
        separately; rounding the summed subtotal instead can drift by a cent.
      */
      var full = sel.length === rows.length;
      var save = 0;
      if (discount > 0 && full) {
        save = sel.reduce(function (s, r) {
          return s + Math.round((Number(r.dataset.price) * discount) / 100);
        }, 0);
      }
      var total = sum - save;

      if (sel.length === 0) {
        // every piece sold out — honest footer, disabled CTA
        countEl.textContent = 'Currently sold out';
        totalEl.textContent = '';
        if (wasEl) wasEl.hidden = true;
        saveWrap.hidden = true;
        if (hintEl) hintEl.hidden = true;
        cta.setAttribute('data-bundle-add', '');
        cta.disabled = true;
        cta.textContent = 'Sold out';
        return;
      }
      countEl.textContent = 'Bundle of ' + sel.length;
      totalEl.dataset.base = total;
      totalEl.textContent = fmt(total);

      /*
        The struck price and the savings line are one unit: they appear together
        on a complete selection and vanish together otherwise. `save > 0` is the
        single test for both, so the card can never strike a price without
        saying what came off it, or claim a saving beside an unstruck total.
      */
      if (save > 0) {
        if (wasEl) {
          wasEl.hidden = false;
          wasEl.dataset.base = sum;
          wasEl.textContent = fmt(sum);
        }
        saveWrap.hidden = false;
        saveAmt.dataset.base = save;
        saveAmt.textContent = fmt(save);
      } else {
        if (wasEl) wasEl.hidden = true;
        saveWrap.hidden = true;
      }

      /*
        The condition stays visible in BOTH states. On a partial selection it
        tells the shopper how to earn the discount; on a complete one it says
        why the total dropped. Hiding it once the discount lands would remove
        the explanation exactly when the number changes. CSS drops it at 390 in
        the complete state only — see mije-stl.css for why.
      */
      if (hintEl) hintEl.hidden = discount <= 0;
      card.classList.toggle('is-discounted', save > 0);
      cta.setAttribute(
        'data-bundle-add',
        sel.map(function (r) {
          return r.dataset.variant;
        }).join(',')
      );
      cta.disabled = sel.length === 0;
    }

    rows.forEach(function (row, i) {
      row.addEventListener('click', function () {
        show(i);
      });
      row.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          show(i);
        }
      });
      /* variant selector — keeps the row's id/price/image/label in sync with
         what "Add the look" will actually put in the bag */
      var vsel = row.querySelector('[data-stl-variant]');
      if (vsel) {
        // the row doubles as the image-browser trigger; using the select
        // must not also jump the pane
        ['click', 'keydown', 'mousedown'].forEach(function (evt) {
          vsel.addEventListener(evt, function (e) {
            e.stopPropagation();
          });
        });
        vsel.addEventListener('change', function (e) {
          e.stopPropagation();
          var opt = vsel.options[vsel.selectedIndex];
          if (!opt) return;
          row.dataset.variant = opt.value;
          row.dataset.price = opt.dataset.price;
          row.dataset.label = opt.dataset.label || opt.textContent.trim();
          if (opt.dataset.image) row.dataset.image = opt.dataset.image;
          renderMeta(row);
          if (index === i) show(i); // pane is on this row — refresh it
          recalc();
        });
      }

      var tick = row.querySelector('[data-stl-tick]');
      tick.addEventListener('click', function (e) {
        e.stopPropagation(); // ticking must not also change the image
        if (tick.disabled) return;
        var on = tick.getAttribute('aria-pressed') === 'true';
        if (on && selectedRows().length === 1) return; // never-zero rule
        tick.setAttribute('aria-pressed', on ? 'false' : 'true');
        tick.querySelector('.mj-stlc__tick').classList.toggle('is-on', !on);
        recalc();
      });
    });

    card.querySelector('[data-stl-prev]').addEventListener('click', function (e) {
      e.stopPropagation();
      show(index - 1);
    });
    card.querySelector('[data-stl-next]').addEventListener('click', function (e) {
      e.stopPropagation();
      show(index + 1);
    });

    /* mobile swipe on the pane */
    var touchX = null;
    pane.addEventListener(
      'touchstart',
      function (e) {
        touchX = e.touches[0].clientX;
      },
      { passive: true }
    );
    pane.addEventListener(
      'touchend',
      function (e) {
        if (touchX === null) return;
        var dx = e.changedTouches[0].clientX - touchX;
        if (Math.abs(dx) > 40) show(index + (dx < 0 ? 1 : -1));
        touchX = null;
      },
      { passive: true }
    );

    /* currency switches re-render the JS-computed amounts */
    window.addEventListener('mije:currency', function () {
      rows.forEach(renderMeta);
      recalc();
      show(index);
    });

    rows.forEach(renderMeta);
    show(0);
    recalc();
  });
})();
